import { shutdown } from "@intercom/messenger-js-sdk"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { replace } from "connected-react-router"
import { push } from "redux-first-history"

import { RoutesPaths } from "../../../constants/user-routes"
import { getUserId, setPassedRegistration } from "../../../hooks/useAttributionTracking"
import { navActions } from "../../../Redux/nav/nav.slice"
import { ErrorsActions } from "../../../Redux/redux/errors.slice"
import api, { request } from "../../../Services/Api"
import {
  getAdminPostLoginRoute,
  getAdminTwoFactorSetupRoute,
  getAdminTwoFactorVerificationRoute
} from "../../../Utils/adminTwoFactor"
import { defaultInnerPage } from "../../../Utils/appRoutes"
import { isOnboardingFinished } from "../../../Utils/onboarding/isOnboardingFinished"
import { adminRoleId, superAdminRoleId } from "../../../Utils/permission"
import utils, {
  createAccountStepPath,
  excludeReferences,
  onboardingStepPath
} from "../../../Utils/utils"
import { setShopifyMoveOffer } from "../../ShopifyMove/redux/shopifyMove.slice"
import { UserSelectors } from "../../User/redux/user.slice"
import { trackEvent } from "../../User/redux/user.thunks"
import * as services from "../services"
import { getOAuthRedirectUrl } from "../utils/socialAuthRedirect.util"
import {
  acceptAdminInviteFailure,
  acceptAdminInviteSuccess,
  authErrorReset,
  authFetchingReset,
  authSetProp,
  changeAdminPasswordFailure,
  changeAdminPasswordSuccess,
  checkEmailInitSuccess,
  clearState,
  commitAdminSecurity2FAFailure,
  commitAdminSecurity2FASuccess,
  confirm2FASetupFailure,
  confirm2FASetupSuccess,
  confirmAdminSecurity2FAFailure,
  confirmAdminSecurity2FASuccess,
  createAccountFailure,
  createAccountSuccess,
  generate2FASetupFailure,
  generate2FASetupSuccess,
  generateAdminSecurity2FAFailure,
  generateAdminSecurity2FASuccess,
  logout,
  refreshTokenFailure,
  refreshTokenSuccess,
  sendConfirmEmailFailure,
  sendConfirmEmailSuccess,
  setShowModalPremiumFeatures,
  signInFailure,
  signInSuccess,
  signUpFailure,
  signUpSuccess,
  socialAuthDeleteSuccess,
  socialAuthFailure,
  socialAuthReset,
  socialAuthSuccess,
  updateProfile,
  updateTokens,
  verify2FAFailure,
  verifyTokenFailure,
  verifyTokenSuccess
} from "./auth.actions"
import { AUTH_SLICE_NAME } from "./auth.constants"

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isAdminUser = (userInfo) => [adminRoleId, 5, superAdminRoleId].includes(userInfo?.role?.id)

export const signIn = createAsyncThunk(
  `${AUTH_SLICE_NAME}/sign_in`,
  async (data, { dispatch, getState }) => {
    try {
      const { redirect, next_page, ...requestData } = data
      const response = await api.signIn(requestData)
      const historyLocal = getState().router

      if (response && response.access && response.userInfo) {
        dispatch(
          signInSuccess({
            userInfo: response.userInfo,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )

        if (response.userInfo?.["is_premium_available"]) {
          dispatch(setShowModalPremiumFeatures(true))
        }

        await delay(500)

        const oauthRedirect = localStorage.getItem("OAuthRedirect")

        if (oauthRedirect) {
          localStorage.removeItem("OAuthRedirect")
          window.location.href = oauthRedirect
        } else if (next_page) {
          window.location.href = next_page
        } else if (redirect) {
          if (!isAdminUser(response.userInfo)) {
            let decodedNextUrl
            try {
              decodedNextUrl = decodeURIComponent(redirect)
            } catch (e) {
              console.error("Error decoding nextUrl:", e)
              decodedNextUrl = redirect
            }

            const responseOauth = await request.api.post(decodedNextUrl, {
              maxRedirects: 0,
              validateStatus: (status) => status >= 200 && status < 400
            })

            if (responseOauth?.data?.redirect_uri) {
              dispatch(utils.goLink(responseOauth?.data?.redirect_uri))
            } else if (
              isOnboardingFinished(response.userInfo) &&
              !isAdminUser(response.userInfo) &&
              response.userInfo?.["email_added"]
            ) {
              const reference = historyLocal?.location?.state?.reference
              if (reference && !excludeReferences.includes(reference)) {
                dispatch(replace(reference))
              } else {
                dispatch(push(defaultInnerPage))
              }
            } else if (isAdminUser(response.userInfo)) {
              dispatch(navActions.navSetProp({ key: "theme", value: "light" }))
              dispatch(push(getAdminPostLoginRoute(response.userInfo)))
            } else if (!response.userInfo?.["email_added"]) {
              dispatch(push("/add-email"))
            } else if (!response.userInfo?.["is_email_verified"]) {
              dispatch(push("/getting_started"))
            } else if (response.userInfo?.["next_step"]) {
              dispatch(push(onboardingStepPath[response.userInfo?.["next_step"]]))
            }
          }
        } else if (
          isOnboardingFinished(response.userInfo) &&
          !isAdminUser(response.userInfo) &&
          response.userInfo?.["email_added"]
        ) {
          const reference = historyLocal?.location?.state?.reference
          if (reference && !excludeReferences.includes(reference)) {
            dispatch(replace(reference))
          } else {
            dispatch(push(defaultInnerPage))
          }
        } else if (isAdminUser(response.userInfo)) {
          dispatch(navActions.navSetProp({ key: "theme", value: "light" }))
          dispatch(push(getAdminPostLoginRoute(response.userInfo)))
        } else if (!response.userInfo?.["email_added"]) {
          dispatch(push("/add-email"))
        } else if (!response.userInfo?.["is_email_verified"]) {
          dispatch(push("/getting_started"))
        } else if (
          response.userInfo?.["next_step"] &&
          !response.userInfo?.is_created_by_invitation
        ) {
          dispatch(push(onboardingStepPath[response.userInfo?.["next_step"]]))
        } else {
          dispatch(push(RoutesPaths.dashboard.root))
        }
      } else if (response && response.data) {
        if (response.status === 401) {
          const errorPayload = response?.data?.message || response?.data

          if (errorPayload?.code === "mfa_required" && errorPayload?.link) {
            const is2FAConfigured =
              typeof errorPayload?.is_2fa_configured === "string"
                ? errorPayload.is_2fa_configured.toLowerCase() === "true"
                : Boolean(errorPayload?.is_2fa_configured)

            dispatch(authSetProp({ key: "ephemeralToken", value: errorPayload.link }))
            dispatch(authSetProp({ key: "is2FAConfigured", value: is2FAConfigured }))
            dispatch(authErrorReset())
            dispatch(
              push(
                is2FAConfigured
                  ? getAdminTwoFactorVerificationRoute(errorPayload.link)
                  : getAdminTwoFactorSetupRoute(errorPayload.link)
              )
            )
            return
          }

          if (response?.data?.code === "password_expired" && response?.data?.isAdmin) {
            dispatch(signInFailure(response.data))
            await delay(500)
            dispatch(authSetProp({ key: "tempEmail", value: requestData?.email }))
            dispatch(authErrorReset())
            await delay(500)
            dispatch(push("/create-new-password"))
          } else {
            dispatch(signInFailure(response.data))
          }
        } else if (response.status === 500) {
          dispatch(signInFailure("Server response error"))
        } else {
          dispatch(signInFailure(response.data))
        }
      } else {
        dispatch(signInFailure("Server response error"))
      }
    } catch (e) {
      if (e?.status === 200 && e?.data?.otp_required) {
        dispatch(signInFailure(e?.data))
      } else {
        dispatch(signInFailure(`Unknown error: ${e}`))
      }
    }
  }
)

export const verify2FA = createAsyncThunk(
  `${AUTH_SLICE_NAME}/verify_2fa`,
  async (data, { dispatch, getState }) => {
    try {
      const state = getState()
      const stateEphemeralToken = state.auth.ephemeralToken
      const pathname = state.router?.location?.pathname || document.location.pathname
      const search = state.router?.location?.search || document.location.search
      const searchParams = new URLSearchParams(search)
      const pathToken = pathname.startsWith("/admin/2fa-verification/")
        ? pathname.replace("/admin/2fa-verification/", "")
        : ""
      const ephemeralToken = decodeURIComponent(
        data?.ephemeral_token || searchParams.get("token") || pathToken || stateEphemeralToken || ""
      )

      const requestData = {
        ...(ephemeralToken && { ephemeral_token: ephemeralToken }),
        ...(data?.otp && { otp: data.otp }),
        ...(data?.recovery_code && { recovery_code: data.recovery_code }),
        ...(typeof data?.trust_device === "boolean" && {
          trust_device: data.trust_device
        })
      }

      const response = await api.verify2FA(requestData)

      if (response?.access && response?.userInfo) {
        dispatch(
          signInSuccess({
            userInfo: response.userInfo,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )

        await delay(500)
        dispatch(navActions.navSetProp({ key: "theme", value: "light" }))
        dispatch(push(getAdminPostLoginRoute(response.userInfo)))
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(verify2FAFailure("Server response error"))
        } else {
          dispatch(verify2FAFailure(response.data))
        }
      } else {
        dispatch(verify2FAFailure("Server response error"))
      }
    } catch (e) {
      dispatch(verify2FAFailure(e?.data || e || "Server response error"))
    }
  }
)

export const generate2FASetup = createAsyncThunk(
  `${AUTH_SLICE_NAME}/generate_2fa_setup`,
  async (data, { dispatch }) => {
    try {
      const response = await api.generate2FASetup(data)

      if (response?.setup_token) {
        dispatch(generate2FASetupSuccess(response))
        return
      }

      if (response?.data) {
        if (response.status === 401) {
          dispatch(generate2FASetupFailure(response.data))
          dispatch(ErrorsActions.errorSave(response?.data?.message || response.data))
          dispatch(replace("/login"))
          return
        }

        if (response.status === 500) {
          dispatch(generate2FASetupFailure("Server response error"))
        } else {
          dispatch(generate2FASetupFailure(response.data))
        }
      } else {
        dispatch(generate2FASetupFailure("Server response error"))
      }
    } catch (e) {
      dispatch(generate2FASetupFailure(e?.data || e || "Server response error"))
    }
  }
)

export const confirm2FASetup = createAsyncThunk(
  `${AUTH_SLICE_NAME}/confirm_2fa_setup`,
  async (data, { dispatch }) => {
    try {
      const response = await api.confirm2FASetup(data)

      if (response?.access && response?.refresh) {
        const userInfo = await api.getCurrentUser()

        if (userInfo?.id || userInfo?.role) {
          dispatch(
            confirm2FASetupSuccess({
              userInfo,
              accessToken: response.access,
              refreshToken: response.refresh,
              recoveryCodes: response.recoveryCodes,
              commitToken: response.commitToken
            })
          )
          dispatch(navActions.navSetProp({ key: "theme", value: "light" }))
          return
        }

        dispatch(confirm2FASetupFailure("Server response error"))
        return
      }

      if (response?.commitToken || response?.recoveryCodes?.length) {
        dispatch(
          confirm2FASetupSuccess({
            userInfo: null,
            accessToken: null,
            refreshToken: null,
            recoveryCodes: response.recoveryCodes,
            commitToken: response.commitToken
          })
        )
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(confirm2FASetupFailure("Server response error"))
        } else {
          dispatch(confirm2FASetupFailure(response.data))
        }
      } else {
        dispatch(confirm2FASetupFailure("Server response error"))
      }
    } catch (e) {
      dispatch(confirm2FASetupFailure(e?.data || e || "Server response error"))
    }
  }
)

export const generateAdminSecurity2FA = createAsyncThunk(
  `${AUTH_SLICE_NAME}/generate_admin_security_2fa`,
  async (_, { dispatch, getState }) => {
    try {
      const { accessToken, refreshToken } = getState().auth

      if (accessToken) {
        api.updateAccessToken(accessToken, refreshToken)
      }

      const response = await api.generateAdminSecurity2FA()

      if (response?.setup_token) {
        dispatch(generateAdminSecurity2FASuccess(response))
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(generateAdminSecurity2FAFailure("Server response error"))
        } else {
          dispatch(generateAdminSecurity2FAFailure(response.data))
        }
      } else {
        dispatch(generateAdminSecurity2FAFailure("Server response error"))
      }
    } catch (e) {
      dispatch(generateAdminSecurity2FAFailure(e?.data || e || "Server response error"))
    }
  }
)

export const confirmAdminSecurity2FA = createAsyncThunk(
  `${AUTH_SLICE_NAME}/confirm_admin_security_2fa`,
  async (data, { dispatch, getState }) => {
    try {
      const { accessToken, refreshToken } = getState().auth

      if (accessToken) {
        api.updateAccessToken(accessToken, refreshToken)
      }

      const response = await api.confirmAdminSecurity2FA(data)

      if (response?.commitToken || response?.recoveryCodes) {
        dispatch(
          confirmAdminSecurity2FASuccess({
            recoveryCodes: response.recoveryCodes,
            commitToken: response.commitToken
          })
        )
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(confirmAdminSecurity2FAFailure("Server response error"))
        } else {
          dispatch(confirmAdminSecurity2FAFailure(response.data))
        }
      } else {
        dispatch(confirmAdminSecurity2FAFailure("Server response error"))
      }
    } catch (e) {
      dispatch(confirmAdminSecurity2FAFailure(e?.data || e || "Server response error"))
    }
  }
)

export const commitAdminSecurity2FA = createAsyncThunk(
  `${AUTH_SLICE_NAME}/commit_admin_security_2fa`,
  async (data, { dispatch, getState }) => {
    try {
      const { accessToken, refreshToken } = getState().auth

      if (accessToken) {
        api.updateAccessToken(accessToken, refreshToken)
      }

      const response = await api.commitAdminSecurity2FA(data)

      if (response?.success && response?.access && response?.refresh) {
        const userInfo = await api.getCurrentUser()

        if (userInfo?.id || userInfo?.role) {
          dispatch(commitAdminSecurity2FASuccess())
          dispatch(
            signInSuccess({
              userInfo,
              accessToken: response.access,
              refreshToken: response.refresh
            })
          )
          return
        }

        dispatch(commitAdminSecurity2FAFailure("Server response error"))
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(commitAdminSecurity2FAFailure("Server response error"))
        } else {
          dispatch(commitAdminSecurity2FAFailure(response.data))
        }
      } else {
        dispatch(commitAdminSecurity2FAFailure("Server response error"))
      }
    } catch (e) {
      dispatch(commitAdminSecurity2FAFailure(e?.data || e || "Server response error"))
    }
  }
)

export const acceptAdminInvite = createAsyncThunk(
  `${AUTH_SLICE_NAME}/accept_admin_invite`,
  async (data, { dispatch }) => {
    try {
      const response = await api.acceptAdminInvite(data)

      if (response?.ephemeralToken) {
        dispatch(acceptAdminInviteSuccess(response.ephemeralToken))
        dispatch(push(getAdminTwoFactorSetupRoute(response.ephemeralToken)))
        return
      }

      if (response?.data) {
        if (response.status === 500) {
          dispatch(acceptAdminInviteFailure("Server response error"))
        } else {
          dispatch(acceptAdminInviteFailure(response.data))
        }
      } else {
        dispatch(acceptAdminInviteFailure("Server response error"))
      }
    } catch (e) {
      dispatch(acceptAdminInviteFailure(e?.data || e || "Server response error"))
    }
  }
)

export const createAccount = createAsyncThunk(
  `${AUTH_SLICE_NAME}/create_account`,
  async ({ data, query, isReferralOutsider }, { dispatch }) => {
    try {
      const frontend_user_id = getUserId()
      const response = await api.createAccount({
        data: {
          ...data,
          frontend_user_id
        },
        query,
        isReferralOutsider
      })
      if (response && response.data.access && response.data.user) {
        window.dataLayer = window?.dataLayer || []
        window.dataLayer.push({
          event: "user_registered",
          email: response.data.user?.email
        })

        dispatch(
          createAccountSuccess({
            userInfo: response.data.user,
            accessToken: response.data.access,
            refreshToken: response.data.refresh
          })
        )
        api.updateAccessToken(response.data.access, response.data.refresh)

        await delay(500)
        dispatch(trackEvent({ event_type: "sign_up" }))
        setPassedRegistration()

        const oauthRedirect = localStorage.getItem("OAuthRedirect")

        if (data?.invite) {
          dispatch(push(`${RoutesPaths.dashboard.root}?invite=${data?.invite}`))
        } else if (oauthRedirect) {
          localStorage.removeItem("OAuthRedirect")
          window.location.href = oauthRedirect
        } else if (process.env.REACT_APP_TYPE === "partner") {
          dispatch(push("/check-mailbox"))
        } else {
          dispatch(push("/getting_started"))
        }
      } else if (response && response.data) {
        if (response.status === 401) {
          dispatch(createAccountFailure(response.data))
        } else if (response.status === 500) {
          dispatch(createAccountFailure("Server response error"))
        } else {
          dispatch(createAccountFailure(response.data))
        }
      } else {
        dispatch(createAccountFailure("Server response error"))
      }
    } catch (e) {
      dispatch(createAccountFailure(`Unknown createAccountFailure: ${e}`))
    }
  }
)

export const resendVerificationLink = createAsyncThunk(
  `${AUTH_SLICE_NAME}/resend_verification_link`,
  async (data, { dispatch }) => {
    try {
      const response = await api.resendVerificationLink(data)
      if (response && response.ok) {
        dispatch(authErrorReset())
      } else if (response && response.data) {
        if (response.status === 401) {
          dispatch(signInFailure(null))
        } else if (response.status === 500) {
          dispatch(signInFailure("Server response error"))
        } else {
          dispatch(signInFailure(response.data))
        }
      } else {
        dispatch(signInFailure("Server response error"))
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

// 🚫 Unused — not dispatched anywhere in the app (only wired into auth.slice.js extraReducers)
export const changeEmail = createAsyncThunk(
  `${AUTH_SLICE_NAME}/change_email`,
  async (data, { dispatch }) => {
    try {
      const response = await api.changeEmail(data)
      if (response && response.ok) {
        dispatch(authErrorReset())
        dispatch(push("/getting_started", data))
      } else if (response && response.data) {
        if (response.status === 401) {
          dispatch(signInFailure(null))
        } else if (response.status === 500) {
          dispatch(signInFailure("Server response error"))
        } else {
          dispatch(signInFailure(response.data))
        }
      } else {
        dispatch(signInFailure("Server response error"))
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

export const sendConfirmEmail = createAsyncThunk(
  `${AUTH_SLICE_NAME}/send_confirm_email`,
  async (data, { dispatch }) => {
    try {
      const email = data.email
      const response = await api.sendConfirmEmail(email)

      if (response?.ok) {
        dispatch(sendConfirmEmailSuccess(response?.data?.time_to_next_try ?? null))
      } else if (response?.data) {
        if (response.status === 401) {
          dispatch(sendConfirmEmailFailure(null))
        } else if (response.status === 500) {
          dispatch(sendConfirmEmailFailure("Server response error"))
        } else {
          dispatch(sendConfirmEmailFailure(response.data))
        }
      } else {
        dispatch(sendConfirmEmailFailure("Server response error"))
      }
    } catch (e) {
      dispatch(sendConfirmEmailFailure(e))
    }
  }
)

export const confirmEmail = createAsyncThunk(
  `${AUTH_SLICE_NAME}/confirm_email`,
  async ({ hash, redirect }, { dispatch }) => {
    try {
      const response = await api.confirmEmail(hash)
      if (response && response.access && response.userInfo) {
        dispatch(
          signInSuccess({
            userInfo: response.userInfo,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )
        dispatch(updateProfile({ next_step: 3 }))
        dispatch(
          push(
            process.env.REACT_APP_TYPE === "partner"
              ? defaultInnerPage
              : "/getting_started?emailVerified=true"
          )
        )
      } else {
        if (response?.data) {
          if (response.status === 401) {
            dispatch(signInFailure(null))
          } else if (response.status === 500) {
            dispatch(signInFailure("Server response error"))
          } else {
            dispatch(signInFailure(response.data))
          }
        } else {
          dispatch(signInFailure("Server response error"))
        }
        if (redirect) {
          dispatch(push(redirect))
        } else {
          dispatch(push("/getting_started"))
        }
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

// 🚫 Unused — not dispatched anywhere in the app (only wired into auth.slice.js extraReducers)
export const confirmEmailInit = createAsyncThunk(
  `${AUTH_SLICE_NAME}/confirm_email_init`,
  async ({ hash, redirect }, { dispatch }) => {
    try {
      dispatch(clearState())
      const response = await api.confirmEmailInit(hash)
      if (response && response?.codeValid) {
        dispatch(checkEmailInitSuccess(response?.codeValid))
      } else {
        if (response?.data) {
          if (response.status === 401) {
            dispatch(signInFailure(null))
            // Original saga dispatched the shared LOGOUT action type here, which
            // rootSaga's own watcher also picks up to run the full logout flow
            // (API call + redirect + Intercom shutdown). That coincidence doesn't
            // exist in RTK, so the equivalent full flow is triggered explicitly.
            dispatch(logoutThunk())
          } else if (response.status === 500) {
            dispatch(signInFailure("Server response error"))
          } else {
            dispatch(signInFailure(response.data))
          }
        } else {
          dispatch(signInFailure("Server response error"))
        }
        if (redirect) {
          dispatch(push(redirect))
        } else {
          dispatch(push("/login"))
        }
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

export const checkPassword = createAsyncThunk(
  `${AUTH_SLICE_NAME}/check_password`,
  async (email, { dispatch }) => {
    try {
      const response = await api.checkPassword(email)
      if (response && response.ok) {
        dispatch(push("/verify-code", email))
        dispatch(authErrorReset())
      } else if (response?.data) {
        if (response.status === 400) {
          dispatch(push("/verify-code", email))
        }
        if (response.status === 401) {
          dispatch(signInFailure(null))
        } else if (response.status === 500) {
          dispatch(signInFailure("Server response error"))
        } else {
          dispatch(signInFailure(response.data))
        }
      } else {
        dispatch(signInFailure("Server response error"))
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

export const confirmPassword = createAsyncThunk(
  `${AUTH_SLICE_NAME}/confirm_password`,
  async (code, { dispatch }) => {
    try {
      const response = await api.confirmPassword(code)
      if (response && response.access && response.userInfo) {
        dispatch(
          signInSuccess({
            userInfo: null,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )
        dispatch(push("/restore-password/code"))
      } else if (response?.data) {
        if (response.status === 401) {
          dispatch(signInFailure(null))
        } else if (response.status === 500) {
          dispatch(signInFailure("Server response error"))
        } else {
          dispatch(signInFailure(response.data))
        }
      } else {
        dispatch(signInFailure("Server response error"))
      }
    } catch (e) {
      dispatch(signInFailure(`Unknown error: ${e}`))
    }
  }
)

export const createUserPassword = createAsyncThunk(
  `${AUTH_SLICE_NAME}/sign_up`,
  async ({ password, isNewUserCheck, confirm_code }, { dispatch }) => {
    try {
      const response = await api.createUserPassword(password, confirm_code)
      if (response && response.ok) {
        if (isNewUserCheck) {
          dispatch(signUpSuccess(response.data))
          await delay(500)
          dispatch(push("/onboarding/plan"))
        } else {
          const successMessage = response?.data?.message?.detail || "Password set successfully!"
          dispatch(signUpSuccess(successMessage))
          await delay(1000)
          dispatch(push("/login"))
        }
      } else if (response?.data) {
        if (response.status === 500) {
          dispatch(signUpFailure("Server response error"))
        } else {
          dispatch(signUpFailure(response.data))
        }
      } else {
        dispatch(signUpFailure("Server response error"))
      }
    } catch (e) {
      dispatch(signUpFailure(`Unknown error: ${e}`))
    }
  }
)

// 🚫 Unused — not dispatched anywhere in the app (call sites are commented out in SignUpPage.js and LoginPage/index.js; only wired into auth.slice.js extraReducers)
export const shopifyAuth = createAsyncThunk(
  `${AUTH_SLICE_NAME}/shopify_auth`,
  async (shop, { dispatch }) => {
    try {
      const response = await api.shopifyAuth(shop)
      if (response?.ok) {
        dispatch(socialAuthReset())
        if (response?.data?.url) window.location.href = response.data.url
      } else {
        dispatch(socialAuthFailure("Server response error"))
      }
    } catch (e) {
      dispatch(socialAuthFailure(`Unknown error: ${e}`))
    }
  }
)

export const socialAuth = createAsyncThunk(
  `${AUTH_SLICE_NAME}/social_auth`,
  async ({ accessToken, socialType, user, isReferralOutsider }, { dispatch, getState }) => {
    try {
      const { successfulRedirectPath } = accessToken ?? {}
      const response = await api.socialAuth(accessToken, socialType, user, isReferralOutsider)
      const historyLocal = getState().router
      const nextCreateAccountStep = UserSelectors.getNextCreateAccountStep(getState())
      let userInfo = response?.userInfo
      if (response?.access && userInfo) {
        dispatch(
          socialAuthSuccess({
            userInfo,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )

        if (socialType === "shopify") {
          window.dataLayer = window?.dataLayer || []
          window.dataLayer.push({
            event: "shopify_app_install",
            email: userInfo?.email,
            user_id: userInfo?.id,
            ...(accessToken?.shop ? { shop_url: accessToken?.shop } : {})
          })
        }

        dispatch(socialAuthReset())
        if (userInfo?.["is_premium_available"]) {
          dispatch(setShowModalPremiumFeatures(true))
        }
        if (response?.data?.url) window?.location?.replace(response?.data?.url)

        await delay(500)
        if (isOnboardingFinished(userInfo) && userInfo?.["email_added"]) {
          if (historyLocal?.location?.state?.reference) {
            dispatch(replace(historyLocal?.location?.state?.reference))
          } else if (nextCreateAccountStep) {
            dispatch(push(createAccountStepPath[nextCreateAccountStep]))
          } else if (successfulRedirectPath) {
            dispatch(push(successfulRedirectPath))
          } else {
            dispatch(push(defaultInnerPage))
          }
        } else if (userInfo?.["email_added"]) {
          if (!userInfo?.["is_email_verified"]) {
            dispatch(push("/getting_started"))
          } else if (userInfo?.["next_step"]) {
            await delay(500)
            dispatch(replace(onboardingStepPath[userInfo?.["next_step"]]))
          }
        } else {
          dispatch(push("/add-email"))
        }
      } else if (response?.data) {
        // A Shopify store lives on one business account at a time. When the one being
        // connected already holds it and this caller may move it, the backend adds
        // `shopify_move` next to `url` — the move popups take over from `url` here.
        if (socialType === "shopify" && response?.data?.["shopify_move"]) {
          dispatch(
            setShopifyMoveOffer({
              offer: response.data["shopify_move"],
              returnUrl: response.data.url
            })
          )
          dispatch(socialAuthReset())
          return
        }
        if (response?.data?.url) {
          const url = response.data.url
          if (url.includes("next_page")) {
            localStorage.setItem("OAuthRedirect", getOAuthRedirectUrl(socialType, accessToken))
            window?.location?.replace("/sign-up")
          } else {
            window?.location?.replace(url)
          }
        }
        if (response.status === 401) {
          const errorPayload = response?.data?.message || response?.data

          if (errorPayload?.code === "mfa_required" && errorPayload?.link) {
            const is2FAConfigured =
              typeof errorPayload?.is_2fa_configured === "string"
                ? errorPayload.is_2fa_configured.toLowerCase() === "true"
                : Boolean(errorPayload?.is_2fa_configured)

            dispatch(authSetProp({ key: "ephemeralToken", value: errorPayload.link }))
            dispatch(authSetProp({ key: "is2FAConfigured", value: is2FAConfigured }))
            dispatch(authErrorReset())
            dispatch(
              push(
                is2FAConfigured
                  ? getAdminTwoFactorVerificationRoute(errorPayload.link)
                  : getAdminTwoFactorSetupRoute(errorPayload.link)
              )
            )
            return
          }

          if (response?.data?.code === "password_expired" && response?.data?.isAdmin) {
            dispatch(socialAuthFailure(response.data))
            await delay(500)
            dispatch(authSetProp({ key: "tempEmail", value: response?.data?.email }))
            dispatch(authErrorReset())
            await delay(500)
            dispatch(push("/create-new-password"))
            return
          }

          dispatch(socialAuthFailure(null))
        } else if (response.status === 500) {
          dispatch(socialAuthFailure("Server response error"))
        } else {
          dispatch(socialAuthFailure(response.data))
        }
      } else {
        dispatch(socialAuthFailure("Server response error"))
      }
    } catch (e) {
      dispatch(socialAuthFailure(`Unknown error: ${e}`))
    }
  }
)

export const logoutThunk = createAsyncThunk(
  `${AUTH_SLICE_NAME}/logout`,
  async (_, { dispatch }) => {
    try {
      await api.logout()
    } catch (e) {
      console.log("error:", e)
    } finally {
      api.updateAccessToken(null, null)
      dispatch(logout())
      localStorage.removeItem("isRedirectedToCompleteBa")
      dispatch(socialAuthReset())
      dispatch(push("/login"))

      try {
        shutdown()
      } catch (e) {
        console.log("intercom shutdown error:", e)
      }
    }
  }
)

// 🚫 Unused — not dispatched anywhere in the app (only wired into auth.slice.js extraReducers)
export const socialAuthConnect = createAsyncThunk(
  `${AUTH_SLICE_NAME}/social_auth_connect`,
  async ({ accessToken, socialType, user }, { dispatch }) => {
    try {
      const response = await api.socialAuthAdd(accessToken, socialType, user)
      if (response?.access && response?.userInfo) {
        dispatch(
          socialAuthSuccess({
            userInfo: response.userInfo,
            accessToken: response.access,
            refreshToken: response.refresh
          })
        )
        await delay(500)
      } else if (response?.data) {
        if (response?.status === 401) {
          dispatch(socialAuthFailure(null))
        } else if (response?.status === 500) {
          dispatch(socialAuthFailure("Server response error"))
        } else {
          dispatch(socialAuthFailure(response.data))
        }
      } else {
        dispatch(socialAuthFailure("Server response error"))
      }
    } catch (e) {
      dispatch(socialAuthFailure(`Unknown error: ${e}`))
    }
  }
)

// 🚫 Unused — not dispatched anywhere in the app (only calls itself recursively and is wired into auth.slice.js extraReducers)
export const socialAuthDelete = createAsyncThunk(
  `${AUTH_SLICE_NAME}/social_auth_delete`,
  async (socialType, { dispatch }) => {
    try {
      const response = await api.socialAuthDelete(socialType)
      if (response?.ok) {
        dispatch(socialAuthDeleteSuccess(response.data))
      } else {
        if (response?.data) {
          if (response?.status === 401) {
            const res = await api.tokenRefresh()
            if (res?.access) {
              dispatch(updateTokens({ accessToken: res.access, refreshToken: res.refresh }))
              await dispatch(socialAuthDelete(socialType))
            } else {
              dispatch(logoutThunk())
              dispatch(push("/login"))
            }
          } else {
            dispatch(ErrorsActions.errorSave(response.data))
          }
        } else {
          dispatch(ErrorsActions.errorSave("Server response error"))
        }
        dispatch(authFetchingReset())
      }
    } catch (e) {
      dispatch(authFetchingReset())
      dispatch(ErrorsActions.errorSave(`Unknown error: ${e}`))
    }
  }
)

export const refreshToken = createAsyncThunk(
  `${AUTH_SLICE_NAME}/refresh_token`,
  async (params, { dispatch }) => {
    try {
      const response = await services.refreshToken(params)

      if (response.ok) {
        dispatch(refreshTokenSuccess(response?.data))
        // reload page after first refresh token
        window.location.reload()
        return
      }

      dispatch(refreshTokenFailure({ errors: response?.data?.message?.detail }))
      // refresh failed (e.g. refresh token expired) -> logout + redirect
      dispatch(logoutThunk())
    } catch (e) {
      // never let an unexpected error skip the logout -> the user must be
      // redirected to login instead of staying with an expired token
      dispatch(refreshTokenFailure({ errors: e?.message || "Refresh failed" }))
      dispatch(logoutThunk())
    }
  }
)

export const verifyToken = createAsyncThunk(
  `${AUTH_SLICE_NAME}/verify_token`,
  async (params, { dispatch }) => {
    try {
      const { token } = params
      const response = await services.verifyToken({ token })

      if (response.status === 200) {
        dispatch(verifyTokenSuccess(response.data))
      }

      if (response.status === 401 || response.status === 403) {
        dispatch(verifyTokenFailure({ errors: "error 401 || 403" }))
        throw response
      }

      if (response.status === 404) {
        throw response
      }
    } catch (errors) {
      dispatch(verifyTokenFailure({ errors }))
      dispatch(refreshToken(params))
    }
  }
)

export const changeAdminPassword = createAsyncThunk(
  `${AUTH_SLICE_NAME}/change_admin_password`,
  async (params, { dispatch }) => {
    try {
      const response = await services.changeAdminPassword(params)
      if (response && response.ok && response?.data) {
        const successMessage = response?.data?.message?.detail || "Password set successfully!"
        dispatch(changeAdminPasswordSuccess(successMessage))
        dispatch(authSetProp({ key: "tempEmail", value: null }))
        await delay(500)
        dispatch(push("/login"))
        return
      } else if (response?.data) {
        if (response.status === 500) {
          dispatch(changeAdminPasswordFailure("Server response error"))
        } else {
          dispatch(changeAdminPasswordFailure(response.data))
        }
      } else {
        dispatch(changeAdminPasswordFailure("Server response error"))
      }
    } catch (errors) {
      dispatch(changeAdminPasswordFailure({ errors }))
    }
  }
)
